import { z } from 'zod';
import * as dt from './data.types.js';
import { dateSchema, integerIdSchema, nullableIntegerIdSchema, uuidSchema, waferFingerprintSchema, containerSlotSchema, waferConflictTypeSchema, conflictStatusSchema, conflictSeveritySchema, resolutionActionSchema, FileMetadataSchema, BaseResponseSchema, firstNameSchema, lastNameSchema, } from './schemas.js';
// Review history entry schema
export const ConflictReviewHistoryEntrySchema = z.object({
    timestamp: dateSchema,
    userId: integerIdSchema,
    userFirstName: firstNameSchema,
    userLastName: lastNameSchema,
    action: z.string().min(1).max(100).trim(), // 'reviewed', 'escalated', 'added_note', etc.
    details: z.string().max(1000).trim().optional(),
});
// Expected vs Actual data schemas for conflict comparison
export const ConflictExpectedDataSchema = z.object({
    waferId: integerIdSchema.optional(),
    fingerprint: waferFingerprintSchema.optional(),
    containerId: integerIdSchema.optional(),
    slotNumber: containerSlotSchema.optional(),
    layerName: z.string().max(255).trim().optional(),
    companyName: z.string().max(255).trim().optional(),
}).optional();
export const ConflictActualDataSchema = z.object({
    scannedFingerprint: waferFingerprintSchema.optional(),
    containerId: integerIdSchema.optional(),
    slotNumber: containerSlotSchema.optional(),
    fingerprintImage: FileMetadataSchema.optional(),
}).optional();
// Base conflict schema
export const WaferConflictSchema = z.object({
    id: integerIdSchema,
    tenantId: uuidSchema,
    conflictType: waferConflictTypeSchema,
    status: conflictStatusSchema,
    severity: conflictSeveritySchema,
    waferId: nullableIntegerIdSchema,
    containerId: nullableIntegerIdSchema,
    containerName: z.string().max(255).nullable().optional(), // Denormalized for display
    waferLayerName: z.string().max(255).nullable().optional(), // Denormalized for display
    slotNumber: containerSlotSchema.nullable(),
    auditRunId: nullableIntegerIdSchema,
    relatedConflictId: nullableIntegerIdSchema,
    supersededByConflictId: nullableIntegerIdSchema,
    supersededAt: dateSchema.nullable(),
    expectedData: ConflictExpectedDataSchema,
    actualData: ConflictActualDataSchema,
    detectedAt: dateSchema,
    detectedByUserId: nullableIntegerIdSchema,
    description: z.string().max(5000).trim().nullable(),
    notes: z.string().max(5000).trim().nullable(),
    resolutionAction: resolutionActionSchema.nullable(),
    resolutionNotes: z.string().max(5000).trim().nullable(),
    resolvedAt: dateSchema.nullable(),
    resolvedByUserId: nullableIntegerIdSchema,
    createdByUserId: nullableIntegerIdSchema,
    fingerprintImage: FileMetadataSchema.nullable(),
    imagePurgedAt: dateSchema.nullable(),
    reviewHistory: z.array(ConflictReviewHistoryEntrySchema),
    createdAt: dateSchema,
    updatedAt: dateSchema,
    deletedAt: dateSchema.nullable(),
});
// Create conflict request
export const CreateWaferConflictRequestSchema = z.object({
    conflictType: waferConflictTypeSchema,
    severity: conflictSeveritySchema.optional().default(dt.CONFLICT_SEVERITY.Medium),
    waferId: nullableIntegerIdSchema,
    containerId: nullableIntegerIdSchema,
    slotNumber: containerSlotSchema.nullable(),
    auditRunId: integerIdSchema,
    expectedData: ConflictExpectedDataSchema,
    actualData: ConflictActualDataSchema,
    description: z.string().min(1).max(5000).trim(),
    fingerprintImage: FileMetadataSchema.nullable(),
});
// Update conflict request
export const UpdateWaferConflictRequestSchema = z.object({
    status: conflictStatusSchema.optional(),
    severity: conflictSeveritySchema.optional(),
    notes: z.string().max(5000).trim().optional(),
    resolutionAction: resolutionActionSchema.optional(),
    resolutionNotes: z.string().max(5000).trim().optional(),
});
// Resolve conflict request
// Ghost wafer creation data (for CreateNewRecord action)
export const GhostWaferCreationDataSchema = z.object({
    type: z.nativeEnum(dt.WAFER_PATTERN_TYPE),
    size: z.nativeEnum(dt.WAFER_SIZE),
    material: z.nativeEnum(dt.WAFER_MATERIAL_TYPE),
    thickness: z.nativeEnum(dt.WAFER_THICKNESS_TYPE),
    layerName: z.string().min(1).max(255).trim(),
    companyAlias: z.string().min(1).max(50).trim(),
    ownerContactVersionId: z.string().min(1).optional(),
}).optional();
export const ResolveWaferConflictRequestSchema = z.object({
    resolutionAction: resolutionActionSchema,
    resolutionNotes: z.string().min(1).max(5000).trim(),
    ghostWaferData: GhostWaferCreationDataSchema,
    /** Required for LinkExistingWafer */
    linkWaferId: integerIdSchema.optional(),
    /** Optional override for UpdateFingerprintFromScan */
    fingerprintOverride: z.string().max(500).optional(),
    /** Mismatch branch: same_wafer | different_wafer */
    mismatchCause: z.enum(['same_wafer', 'different_wafer']).optional(),
});
/**
 * Get valid resolution actions for a specific conflict type
 */
export function getMismatchActionsForCause(cause) {
    if (cause === 'same_wafer') {
        return [
            dt.RESOLUTION_ACTION.UpdateFingerprintFromScan,
            dt.RESOLUTION_ACTION.DismissInvalidScan,
        ];
    }
    return [dt.RESOLUTION_ACTION.ReassignSlotOccupancy];
}
export function getValidActionsForConflictType(conflictType) {
    const validCombinations = {
        [dt.WAFER_CONFLICT_TYPE.GhostWafer]: [
            dt.RESOLUTION_ACTION.LinkExistingWafer,
            dt.RESOLUTION_ACTION.CreateNewRecord,
        ],
        [dt.WAFER_CONFLICT_TYPE.MissingWafer]: [
            dt.RESOLUTION_ACTION.MarkAsLost,
            dt.RESOLUTION_ACTION.MarkAsDispatched,
            dt.RESOLUTION_ACTION.MarkAsUnlocated,
        ],
        [dt.WAFER_CONFLICT_TYPE.Mismatch]: [
            dt.RESOLUTION_ACTION.UpdateFingerprintFromScan,
            dt.RESOLUTION_ACTION.DismissInvalidScan,
            dt.RESOLUTION_ACTION.ReassignSlotOccupancy,
        ],
        [dt.WAFER_CONFLICT_TYPE.Duplicate]: [
            dt.RESOLUTION_ACTION.RemoveFromDatabase,
            dt.RESOLUTION_ACTION.NoActionRequired,
        ],
        [dt.WAFER_CONFLICT_TYPE.Lost]: [
            dt.RESOLUTION_ACTION.MarkAsLost,
            dt.RESOLUTION_ACTION.RemoveFromDatabase,
            dt.RESOLUTION_ACTION.NoActionRequired,
        ],
        [dt.WAFER_CONFLICT_TYPE.TransferFailure]: [
            dt.RESOLUTION_ACTION.RetryTransfer,
            dt.RESOLUTION_ACTION.CorrectDatabase,
            dt.RESOLUTION_ACTION.NoActionRequired,
        ],
    };
    return validCombinations[conflictType] || [];
}
/**
 * Get actions that are valid for ALL provided conflict types (intersection)
 * Useful for bulk operations where action must be valid for all selected conflicts
 */
export function getCommonValidActions(conflictTypes) {
    if (conflictTypes.length === 0) {
        return [];
    }
    // Actions that require unique data per conflict and cannot be used in bulk operations
    const bulkIncompatibleActions = [
        dt.RESOLUTION_ACTION.CreateNewRecord, // Each ghost wafer needs unique information
    ];
    // Start with actions valid for the first conflict type
    let commonActions = getValidActionsForConflictType(conflictTypes[0]);
    // Intersect with actions valid for each subsequent conflict type
    for (let i = 1; i < conflictTypes.length; i++) {
        const validForType = getValidActionsForConflictType(conflictTypes[i]);
        commonActions = commonActions.filter(action => validForType.includes(action));
    }
    // Filter out actions that are incompatible with bulk operations
    commonActions = commonActions.filter(action => !bulkIncompatibleActions.includes(action));
    return commonActions;
}
/**
 * Validate if a resolution action is valid for a conflict type
 */
export function isValidActionForConflictType(conflictType, resolutionAction) {
    const validActions = getValidActionsForConflictType(conflictType);
    return validActions.includes(resolutionAction);
}
// Get conflicts request (query params)
export const GetWaferConflictsRequestSchema = z.object({
    page: z.coerce.number().int().min(1).optional().default(1),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
    search: z.string().optional(),
    conflictType: waferConflictTypeSchema.optional(),
    status: conflictStatusSchema.optional(),
    severity: conflictSeveritySchema.optional(),
    containerId: integerIdSchema.optional(),
    waferId: integerIdSchema.optional(),
    auditRunId: integerIdSchema.optional(),
    excludeSuperseded: z.coerce.boolean().optional().default(true),
    detectedAfter: dateSchema.optional(),
    detectedBefore: dateSchema.optional(),
    resolvedAfter: dateSchema.optional(),
    resolvedBefore: dateSchema.optional(),
    sortBy: z.enum(['detectedAt', 'resolvedAt', 'severity', 'conflictType', 'status']).optional().default('detectedAt'),
    sortOrder: z.enum(['ASC', 'DESC']).optional().default('DESC'),
});
export const ConflictIdParamSchema = z.object({
    id: integerIdSchema,
});
export const ConflictStatisticsQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(365).optional().default(30),
});
export const DismissConflictBodySchema = z.object({
    notes: z.string().max(5000).trim().optional(),
});
export const EscalateConflictBodySchema = z.object({
    notes: z.string().max(5000).trim().optional(),
});
// Bulk operations
export const BulkResolveConflictsRequestSchema = z.object({
    conflictIds: z.array(integerIdSchema).min(1).max(100),
    resolutionAction: resolutionActionSchema,
    resolutionNotes: z.string().max(5000).trim(),
});
export const BulkDismissConflictsRequestSchema = z.object({
    conflictIds: z.array(integerIdSchema).min(1).max(100),
    notes: z.string().max(5000).trim().optional(),
});
// Conflict statistics schema
export const ConflictStatisticsSchema = z.object({
    totalConflicts: z.number().int().min(0),
    openConflicts: z.number().int().min(0),
    underReviewConflicts: z.number().int().min(0),
    resolvedConflicts: z.number().int().min(0),
    dismissedConflicts: z.number().int().min(0),
    escalatedConflicts: z.number().int().min(0),
    byType: z.record(waferConflictTypeSchema, z.number().int().min(0)),
    bySeverity: z.record(conflictSeveritySchema, z.number().int().min(0)),
    avgResolutionTimeHours: z.number().min(0).nullable(),
    conflictsByDay: z.array(z.object({
        date: z.string(), // ISO date string
        count: z.number().int().min(0),
    })),
});
// Resolution template schema
export const ResolutionTemplateSchema = z.object({
    id: z.string().min(1).max(100).trim(),
    name: z.string().min(1).max(255).trim(),
    description: z.string().min(1).max(1000).trim(),
    applicableTypes: z.array(waferConflictTypeSchema),
    resolutionAction: resolutionActionSchema,
    defaultNotes: z.string().max(5000).trim().optional(),
});
// API Response schemas
export const CreateWaferConflictResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        conflict: WaferConflictSchema,
    }),
});
export const GetWaferConflictResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        conflict: WaferConflictSchema,
    }),
});
export const GetWaferConflictsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        conflicts: z.array(WaferConflictSchema),
        total: z.number().int().min(0),
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        totalPages: z.number().int().min(0),
    }),
});
export const UpdateWaferConflictResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        conflict: WaferConflictSchema,
    }),
});
export const ResolveWaferConflictResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        conflict: WaferConflictSchema,
    }),
});
export const ResolveConflictGroupResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        conflicts: z.array(WaferConflictSchema),
    }),
});
export const BulkResolveConflictsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        resolvedCount: z.number().int().min(0),
        failedCount: z.number().int().min(0),
        conflicts: z.array(WaferConflictSchema),
    }),
});
export const ConflictStatisticsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        statistics: ConflictStatisticsSchema,
    }),
});
export const ResolutionTemplatesResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        templates: z.array(ResolutionTemplateSchema),
    }),
});
// Type exports
// Audit conflict creation schemas
export const AuditConflictResultSchema = z.object({
    slotNumber: containerSlotSchema,
    auditState: waferConflictTypeSchema,
    expectedFingerprint: waferFingerprintSchema.optional(),
    actualFingerprint: waferFingerprintSchema.optional(),
    expectedWaferId: integerIdSchema.optional(),
    actualWaferId: integerIdSchema.optional(),
    fingerprintImage: FileMetadataSchema.optional(),
});
export const CreateConflictsFromAuditRequestSchema = z.object({
    auditResults: z.array(AuditConflictResultSchema).min(1),
    containerId: integerIdSchema,
    auditRunId: integerIdSchema,
});
export const CreateConflictsFromAuditResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        count: z.number().int().min(0),
        createdConflicts: z.array(z.object({
            id: integerIdSchema,
            conflictType: waferConflictTypeSchema,
            waferId: integerIdSchema.optional(),
            containerId: integerIdSchema.optional(),
        })),
    })
});
//# sourceMappingURL=conflict-schemas.js.map